import { INPUT_VARIANT_OPTIONS_MAP, LANGUAGE_EN, LANGUAGE_NO, LANGUAGE_OPTIONS, MEDIA_SIZE_M, ORDER_ID_LABELS } from "../constants";
import _ from "lodash";
import { getAppWidth, getLoginServiceData, isLoginPage, isSuccessPage } from "./appCore";
import { BRAND_ROUNDED_STYLE_TYPES, COOKIES_POLICIES as COOKIE_POLICIES, EXCHANGE_PROPERTY_PICKER_TYPES, INGRID_SUPPORT_EMAIL } from "rylak-common/src/constants";
import chroma from "chroma-js";

export function getBrandData(appProps) {
    return appProps?.brandData;
}

export function getBackgroundImage(appProps) {
    const { brandData } = appProps;

    if (brandData?.backgroundImageMobile) {
        if (getAppWidth(appProps) < MEDIA_SIZE_M) {
            return brandData?.backgroundImageMobile;
        }
    }
    return (isLoginPage(appProps) && brandData?.startBackground) || (isSuccessPage(appProps) && brandData?.successBackground) || brandData?.backgroundImage;
}

export function getGeneralBackgroundImage(appProps) {
    return getBrandData(appProps)?.backgroundImage;
}

export function getBrandBackgroundFrameColor(appProps) {
    return getBrandData(appProps)?.backgroundFrameColor || "#fff";
}

export function getBrandBackgroundOpacity(appProps) {
    return getBrandData(appProps)?.backgroundImageOpacity;
}

export function hasBrandBackgroundOpacity(appProps) {
    return getBrandBackgroundOpacity(appProps) !== 100;
}

export function isBrandFrameBackgroundSolidWhite(appProps) {
    const frameRgbColor = chroma(getBrandBackgroundFrameColor(appProps))._rgb;
    const colorIsWhite = frameRgbColor[0] === 255 && frameRgbColor[1] === 255 && frameRgbColor[2] === 255 && frameRgbColor[3] === 1;
    const hasBackgroundOpacity = hasBrandBackgroundOpacity(appProps);
    return colorIsWhite && !hasBackgroundOpacity;
}

export function getBrandName(appProps) {
    return getBrandData(appProps)?.displayName;
}

export function getBrandHomePageUrl(appProps) {
    return getBrandData(appProps)?.homePage;
}

export function showAllOrdersBrand(appProps) {
    return !!getBrandData(appProps)?.showAllOrders;
}

export function getOrderIdLabelIds() {
    return Object.values(ORDER_ID_LABELS);
}

export function getOrderIdLabelId(appProps) {
    let labelId = getBrandData(appProps)?.orderIdText;
    if (!getOrderIdLabelIds().includes(labelId)) {
        return ORDER_ID_LABELS.ORDER_ID;
    }
    return labelId;
}

export function getAllLanguageIds() {
    return _.values(LANGUAGE_OPTIONS).map((o) => o.value);
}

export function getEnabledLanguages(appProps) {
    const { brandData } = appProps;
    if (!brandData) {
        return [LANGUAGE_EN];
    }
    let allLanguages = getAllLanguageIds();
    let enabledLanguages = brandData?.enabledLanguages?.length > 0 ? brandData?.enabledLanguages : allLanguages;

    enabledLanguages = enabledLanguages.filter((l) => allLanguages.includes(l));
    if (!enabledLanguages.length) {
        // If something is buggy, only allow english
        return [LANGUAGE_EN];
    }
    return enabledLanguages.sort();
}

export function getDefaultLanguage(appProps) {
    const { brandData, languageOverride } = appProps;
    if (languageOverride) {
        return languageOverride;
    }
    if (!brandData) {
        return LANGUAGE_EN;
    }
    const enabledLanguages = getEnabledLanguages(appProps);
    const defaultLanguage = brandData?.defaultLanguage;
    if (defaultLanguage && enabledLanguages.includes(defaultLanguage)) {
        return defaultLanguage;
    }
    if (enabledLanguages.includes(LANGUAGE_EN)) {
        return LANGUAGE_EN;
    }
    return enabledLanguages[0];
}

export function getBrandButtonType(appProps) {
    return getBrandData(appProps)?.buttonType || BRAND_ROUNDED_STYLE_TYPES.ROUNDED;
}

export function getBrandInputType(appProps) {
    return getBrandData(appProps)?.inputType || BRAND_ROUNDED_STYLE_TYPES.ROUNDED;
}

export function getBrandOverallType(appProps) {
    return getBrandData(appProps)?.overallType || BRAND_ROUNDED_STYLE_TYPES.VERY_ROUNDED;
}

export function getBrandCheckboxType(appProps) {
    return getBrandData(appProps)?.checkboxType || BRAND_ROUNDED_STYLE_TYPES.SLIGHTLY_ROUNDED;
}

export function getBrandWindowType(appProps) {
    return getBrandData(appProps)?.windowType || BRAND_ROUNDED_STYLE_TYPES.VERY_ROUNDED;
}

export function brandDesignTypeToBorderPx(buttonType) {
    return {
        [BRAND_ROUNDED_STYLE_TYPES.SHARP]: "0px",
        [BRAND_ROUNDED_STYLE_TYPES.SLIGHTLY_ROUNDED]: "3px",
        [BRAND_ROUNDED_STYLE_TYPES.ROUNDED]: "6px",
        [BRAND_ROUNDED_STYLE_TYPES.VERY_ROUNDED]: "10px",
        [BRAND_ROUNDED_STYLE_TYPES.FULLY_ROUNDED]: "50px",
    }[buttonType];
}

export function getSupportEmail(appProps) {
    return getBrandData(appProps)?.supportEmail || INGRID_SUPPORT_EMAIL;
}

export function isLanguageCodeEnabledOnBrand(appProps, languageCode) {
    return !!getEnabledLanguages(appProps)?.find((enabledLanguageCode) => enabledLanguageCode?.toUpperCase() === languageCode?.toUpperCase());
}

// Translate a BCP-47 style locale ("en-US", "de-AT") to an internal language code.
// Tries the regional variant first when enabled (e.g. DE_AT), otherwise falls back to the base (DE).
// Norwegian "nb" / "nn" both collapse to NO.
export function normalizeLocaleToLanguageCode(localeOrCode, enabledLanguages = []) {
    if (!localeOrCode) return localeOrCode;
    const upper = localeOrCode.toUpperCase();

    if (!upper.includes("-")) {
        if (upper === "NB" || upper === "NN") return LANGUAGE_NO;
        return upper;
    }

    const [base, region] = upper.split("-");
    if (base === "NB" || base === "NN") return LANGUAGE_NO;

    const regional = `${base}_${region}`;
    if (enabledLanguages?.some((l) => l?.toUpperCase() === regional)) {
        return regional;
    }
    return base;
}

export function isPriceListExchangesDisabled(appProps) {
    return getLoginServiceData(appProps)?.exchangesDisabled;
}

export function isPriceListComplaintsDisabled(appProps) {
    return getLoginServiceData(appProps)?.complaintsDisabled;
}

export function isPriceListExchangesComplaintDisabled(appProps) {
    return getLoginServiceData(appProps)?.exchangesComplaintDisabled;
}

export function getBrandButtonTextType(appProps) {
    return getBrandData(appProps)?.buttonTextType;
}

export function getBrandButtonEffects(appProps) {
    return getBrandData(appProps)?.buttonEffects;
}

export function getBrandButtonVariant(appProps) {
    return getBrandData(appProps)?.buttonVariant;
}

export function getBrandFont(appProps) {
    return getBrandData(appProps)?.fontNamePrimary;
}

export function getBrandButtonFont(appProps) {
    return getBrandData(appProps)?.buttonFont;
}

export function getBrandButtonThickness(appProps) {
    return getBrandData(appProps)?.buttonThickness;
}

export function getBrandInputVariant(appProps) {
    return getBrandData(appProps)?.inputVariant;
}

export function isInputVariantDefault(appProps) {
    return getBrandInputVariant(appProps) === INPUT_VARIANT_OPTIONS_MAP.DEFAULT.value;
}

export function showIngridLogo(appProps) {
    return getBrandData(appProps)?.showIngridLogo || _.isUndefined(getBrandData(appProps)?.showIngridLogo);
}

export function cookiesIsDisabled(appProps) {
    return getBrandData(appProps)?.cookiesPolicy === COOKIE_POLICIES.DISABLED;
}

export function getBrandReturnsPolicyUrl(appProps) {
    return getBrandData(appProps)?.returnPolicyUrl;
}

export function getBrandTermsAndConditionsUrl(appProps) {
    return getBrandData(appProps)?.termsAndConditionsUrl;
}

export function getBrandPrivacyPolicyUrl(appProps) {
    return getBrandData(appProps)?.privacyPolicyUrl;
}

export function getBrandFrameBorderEffect(appProps) {
    return getBrandData(appProps)?.frameBorderEffect;
}

export function getBrandFramePaddingMobile(appProps) {
    return getBrandData(appProps)?.framePaddingMobile;
}

export function getBrandFramePaddingPc(appProps) {
    return getBrandData(appProps)?.framePaddingPc;
}

export function getBrandProductImageObjectFit(appProps) {
    return getBrandData(appProps)?.productImageObjectFit || "COVER";
}

export function getBrandProductImageAspectRatio(appProps) {
    function isValidNumberPair(str) {
        return /^\d+:\d+$/.test(str);
    }

    const aspectRatio = getBrandData(appProps)?.productImageAspectRatio;
    if (isValidNumberPair(aspectRatio)) {
        return aspectRatio;
    }
    return "1:1";
}

export function getBrandShouldForceDropdownPropertyPicker(appProps) {
    return getBrandData(appProps)?.exchangePropertyPicker === EXCHANGE_PROPERTY_PICKER_TYPES.FORCE_DROPDOWNS;
}
